import React, { useEffect, useRef, useState, Suspense, lazy } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Mic,
  Square,
  Play,
  User as UserIcon,
  Sparkles,
  Trophy,
  AlertTriangle,
  Video,
  VideoOff,
  Volume2,
  VolumeX,
  RotateCcw,
  Smile,
  Frown,
  Meh,
  Loader2,
} from "lucide-react";

// Lazy-load Spline so the heavy 3D runtime only ships when MockInterview is opened.
const Spline = lazy(() => import("@splinetool/react-spline"));
const SPLINE_ROBOT_SCENE = "https://prod.spline.design/OtYuP0VntfPZUKqH/scene.splinecode";
const OscePanels = lazy(() => import("../components/OscePanels"));
const LearningDashboard = lazy(() => import("../components/LearningDashboard"));

const FACE_MODEL_URL =
  "https://justadudewhohacks.github.io/face-api.js/models";

const EMOTION_COLORS = {
  happy: "#34C759",
  neutral: "#0033FF",
  surprised: "#FFD60A",
  sad: "#5E5CE6",
  angry: "#FF3B30",
  fearful: "#FF9500",
  disgusted: "#8E8E93",
};

export default function MockInterview() {
  const { user } = useAuth();
  const [portrait, setPortrait] = useState(null);
  const [quota, setQuota] = useState(null);
  const [jobTitle, setJobTitle] = useState("");
  const [difficulty, setDifficulty] = useState("friendly");
  const [language, setLanguage] = useState("en"); // en | ar | de
  const [session, setSession] = useState(null); // {session_id, turn}
  const [messages, setMessages] = useState([]); // {role, content}
  const [thinking, setThinking] = useState(false);
  const [recording, setRecording] = useState(false);  const [transcribing, setTranscribing] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [webcamOn, setWebcamOn] = useState(false);
  const [result, setResult] = useState(null);
  const [emotions, setEmotions] = useState(null);
  const [faceLoaded, setFaceLoaded] = useState(false);
  const [faceError, setFaceError] = useState(false);
  const [voiceMode, setVoiceMode] = useState(true);   // ChatGPT-style fullscreen voice UI
  const [speaking, setSpeaking] = useState(false);    // is Marcus's TTS currently playing

  // ----- Pro-Vertical modes — OSCE-style / Code review / Moot court / Finance pitch
  // mode: "chat" | "german_medical_case" | "engineering_case" | "law_case" | "finance_case"
  const [mode, setMode] = useState("chat");
  const [roleLevel, setRoleLevel] = useState("assistenzarzt"); // assistenzarzt | facharzt
  const [codeLanguage, setCodeLanguage] = useState("python");  // for engineering_case
  const [phase, setPhase] = useState(null);
  const [phaseLabel, setPhaseLabel] = useState("");
  const [prepCountdown, setPrepCountdown] = useState(0);
  const [showMedicalPaywall, setShowMedicalPaywall] = useState(false);
  const [verticalCaseMeta, setVerticalCaseMeta] = useState(null);  // holds case_code, case_facts, case_scenario
  // OSCE v2: visual paper panels (vitals / EKG / Laborwerte / Medikationsplan)
  const [casePanels, setCasePanels] = useState(null);
  const [ecgLockedKey, setEcgLockedKey] = useState("");
  // Learning History / Case Mastery dashboard toggle on the setup screen
  const [showDashboard, setShowDashboard] = useState(false);

  // Pro Vertical tier — Medical Pro €150/mo OR Medical Bundle €270/mo
  // (50 Marcus interviews + 20 medical OSCE cases). Same flag also unlocks
  // Engineering / Law / Finance simulators for Bundle / override users.
  const hasProVertical = (u) => {
    if (!u) return false;
    const sub = (u.subscription || "free").toLowerCase();
    if (sub === "pro_vertical" || sub === "medical_pro") return true;
    if (u.medical_pro_override === true) return true;
    if (u.vertical_pro_override === true) return true;
    return false;
  };
  const hasMedicalPro = hasProVertical; // backward-compat alias for old prop name

  // Refs that mirror state so closures (timers, recursive setTimeouts) read live values
  const speakingRef = useRef(false);
  const thinkingRef = useRef(false);
  const transcribingRef = useRef(false);
  const recordingRef = useRef(false);
  const voiceModeRef = useRef(true);
  useEffect(() => { speakingRef.current = speaking; }, [speaking]);
  useEffect(() => { recordingRef.current = recording; }, [recording]);
  useEffect(() => { transcribingRef.current = transcribing; }, [transcribing]);
  useEffect(() => { thinkingRef.current = thinking; }, [thinking]);
  useEffect(() => { voiceModeRef.current = voiceMode; }, [voiceMode]);

  // Session ref — sendAnswer needs the latest session id at the moment the
  // user finishes speaking, not whatever was captured when the recorder was wired.
  const sessionRef = useRef(null);
  useEffect(() => { sessionRef.current = session; }, [session]);

  // Preparation countdown for German Medical mode (5 min between Anamnese and Chefarzt).
  // When it hits 0 we send a synthetic "Ich bin bereit" so the backend transitions
  // to the presentation phase and the Chef-Arzt opens with a question.
  useEffect(() => {
    if (prepCountdown <= 0) return;
    const t = setInterval(() => setPrepCountdown((v) => Math.max(0, v - 1)), 1000);
    return () => clearInterval(t);
  }, [prepCountdown]);
  useEffect(() => {
    if (phase === "preparation" && prepCountdown === 0 && session?.session_id) {
      // Auto-transition: send a "ready to present" answer so backend swaps to chefarzt phase.
      const sid = session.session_id;
      (async () => {
        try {
          await sendAnswerRef.current?.("Ich bin bereit, den Fall vorzustellen.");
        } catch { /* ignore — user can also click manually */ }
      })();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, prepCountdown]);
  // Hold a stable ref to sendAnswer so the prep-timer effect can call it without re-firing.
  const sendAnswerRef = useRef(null);

  const [typing, setTyping] = useState(false);
  const [typedAnswer, setTypedAnswer] = useState("");

  const submitTyped = async (e) => {
    e?.preventDefault();
    if (!typedAnswer.trim()) return;
    await sendAnswer(typedAnswer.trim());
    setTypedAnswer("");
    setTyping(false);
  };

  const videoRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioStreamRef = useRef(null);
  const recorderRef = useRef(null);
  const recordChunksRef = useRef([]);
  const detectIntervalRef = useRef(null);
  const emotionsRollingRef = useRef([]);
  const faceApiRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => {
    api
      .get("/mock-interview/portrait")
      .then((r) => setPortrait(r.data.data_url))
      .catch(() => {});
    api
      .get("/mock-interview/quota")
      .then((r) => setQuota(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, thinking]);

  // Lazy-load face-api.js when webcam turns on
  useEffect(() => {
    if (!webcamOn) return;
    let cancelled = false;
    (async () => {
      try {
        if (!faceApiRef.current) {
          const faceapi = await import("face-api.js");
          faceApiRef.current = faceapi;
          await Promise.all([
            faceapi.nets.tinyFaceDetector.loadFromUri(FACE_MODEL_URL),
            faceapi.nets.faceExpressionNet.loadFromUri(FACE_MODEL_URL),
          ]);
        }
        if (!cancelled) setFaceLoaded(true);
      } catch (err) {
        console.warn("face-api load failed", err);
        if (!cancelled) setFaceError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [webcamOn]);

  const audioElRef = useRef(null);
  const audioUnlockedRef = useRef(false);

  // ---- Lip-sync / TTS audio analyser (drives the Spline robot's pulse) ----
  const splineAppRef = useRef(null);          // Spline scene runtime
  const splineWrapRef = useRef(null);         // <div> wrapping <Spline /> — we mutate transform
  const ttsAudioCtxRef = useRef(null);
  const ttsAnalyserRef = useRef(null);
  const ttsSourceConnectedRef = useRef(false);

  const _attachTTSAnalyser = async () => {
    // Hook a Web Audio analyser onto the persistent <audio> element exactly once.
    // We use captureStream() (not createMediaElementSource) so the original audio
    // keeps flowing to the speakers — we just tap a parallel stream for analysis.
    if (ttsSourceConnectedRef.current) {
      // Make sure the context is running (autoplay policy may have suspended it)
      try { await ttsAudioCtxRef.current?.resume?.(); } catch { /* ignore */ }
      return;
    }
    const audioEl = audioElRef.current;
    if (!audioEl || typeof audioEl.captureStream !== "function") return;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      try { await ctx.resume(); } catch { /* ignore */ }
      const stream = audioEl.captureStream();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.6;
      src.connect(analyser);
      // IMPORTANT: do NOT connect analyser to ctx.destination — the original
      // <audio> element is already routing to speakers. Connecting again would
      // double-play. We only need amplitude data here.
      ttsAudioCtxRef.current = ctx;
      ttsAnalyserRef.current = analyser;
      ttsSourceConnectedRef.current = true;
    } catch (e) {
      // captureStream not available or another graph issue — analyser disabled,
      // but TTS audio still plays normally via the <audio> element default path.
      console.warn("TTS analyser hook failed (audio still plays normally)", e);
    }
  };

  // Lip-sync animation loop: while Marcus is speaking, scale + glow the robot
  // proportional to instantaneous voice amplitude.
  useEffect(() => {
    if (!speaking) {
      const wrap = splineWrapRef.current;
      if (wrap) {
        wrap.style.transform = "scale(1)";
        wrap.style.filter = "drop-shadow(0 0 18px rgba(91,140,255,0.25))";
      }
      return;
    }
    let raf = 0;
    const data = new Uint8Array(128);
    const tick = () => {
      const a = ttsAnalyserRef.current;
      const wrap = splineWrapRef.current;
      if (a && wrap) {
        a.getByteFrequencyData(data);
        // Voice band: ~150Hz–2kHz lives roughly in bins 5–60 at 256 fft @ 48kHz
        let sum = 0;
        for (let i = 5; i < 60; i++) sum += data[i];
        const avg = sum / 55 / 255; // 0..1
        const scale = 1 + Math.min(avg * 0.12, 0.12);
        const glow = 18 + avg * 40;
        const alpha = 0.25 + Math.min(avg * 0.6, 0.6);
        wrap.style.transform = `scale(${scale.toFixed(3)})`;
        wrap.style.filter = `drop-shadow(0 0 ${glow.toFixed(1)}px rgba(91,140,255,${alpha.toFixed(2)}))`;

        // If the Spline scene exposes any of the common voice/particle variables,
        // drive them every frame from the live voice amplitude (0..1).
        const sp = splineAppRef.current;
        if (sp && typeof sp.setVariable === "function") {
          const v = avg;                  // 0..1 instantaneous amplitude
          const v100 = Math.round(avg * 100); // some scenes use 0..100 ranges
          const tryVars = [
            ["mouthOpen", v], ["mouth", v], ["talk", v], ["talking", v],
            ["voice", v], ["audio", v], ["amplitude", v], ["volume", v],
            ["intensity", v], ["energy", v], ["pulse", v],
            ["particles", v], ["particle", v], ["emit", v], ["emitRate", v100],
            ["scale", 1 + v * 0.3], ["speed", v],
          ];
          for (const [name, value] of tryVars) {
            try { sp.setVariable(name, value); } catch { /* scene doesn't expose this var — silently skip */ }
          }
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [speaking]);

  // Trigger ref to schedule auto-rearm when Marcus stops speaking (used by speak()).
  const _scheduleAutoArm = () => {
    if (!voiceModeRef.current) return;
    setTimeout(() => {
      if (!voiceModeRef.current) return;
      if (speakingRef.current || recordingRef.current ||
          transcribingRef.current || thinkingRef.current) return;
      // Only arm if interview is actually live (session set, no result yet).
      // We can check via DOM — the orb only renders when both are true.
      const orb = document.querySelector('[data-testid="mi-orb"]');
      if (!orb) return;
      console.log("[Marcus] auto-rearming mic");
      startRecording();
    }, 500);
  };

  const _onSplineLoad = (sp) => {
    splineAppRef.current = sp;

    // Best-effort: find any object whose name looks like an orbit/ring and hide it.
    // Spline doesn't give us a public list-objects API, so we try common names.
    const namesToHide = [
      "Ring", "Rings", "Ring 1", "Ring 2", "Ring 3", "Ring1", "Ring2", "Ring3",
      "Ring 01", "Ring 02", "Ring 03", "Ring01", "Ring02", "Ring03",
      "Orbit", "Orbits", "Orbit 1", "Orbit 2", "Orbit1", "Orbit2",
      "Particles", "Particle", "Particle System", "ParticleSystem",
      "Trail", "Trails", "Stars", "Star",
    ];
    const hideObj = (obj) => {
      if (!obj) return false;
      try { obj.visible = false; } catch { /* ignore */ }
      try { if (obj.scale) { obj.scale.x = 0; obj.scale.y = 0; obj.scale.z = 0; } } catch { /* ignore */ }
      return true;
    };
    let hidden = 0;
    for (const name of namesToHide) {
      try {
        const obj = sp.findObjectByName?.(name);
        if (obj) { hideObj(obj); hidden++; }
      } catch { /* ignore */ }
    }
    // Also walk children of the scene if accessible.
    try {
      const root = sp._scene || sp.scene;
      const traverse = (n) => {
        if (!n) return;
        const nm = (n.name || "").toLowerCase();
        if (/(ring|orbit|trail|particle|star)/.test(nm)) { hideObj(n); hidden++; }
        (n.children || []).forEach(traverse);
      };
      if (root) traverse(root);
    } catch { /* ignore */ }
    if (process.env.NODE_ENV !== "production") {
      console.log(`[Spline] hid ${hidden} orbit/ring/particle nodes`);
    }

    // Try to silence any default "wave" / "idle wave" animation by emitting a stop event.
    try { sp.emitEvent("mouseDown", "Stop"); } catch { /* ignore */ }
    try { sp.emitEvent("keyDown", "Stop"); } catch { /* ignore */ }
    try { sp.setVariable("wave", 0); } catch { /* ignore */ }
    try { sp.setVariable("isWaving", false); } catch { /* ignore */ }
  };

  // Get-or-create a single persistent <audio> element. Created only on a
  // real user gesture so the browser autoplay policy lets us play later.
  const _ensureAudioEl = () => {
    if (audioElRef.current) return audioElRef.current;
    const a = document.createElement("audio");
    a.preload = "auto";
    a.playsInline = true;
    // Don't append to DOM — keep in memory so unmount-on-navigate doesn't kill it.
    audioElRef.current = a;
    return a;
  };

  // Run on the first start-interview click to "unlock" the audio element so
  // subsequent .play() calls (after async fetches) work without user gesture.
  const _unlockAudio = () => {
    if (audioUnlockedRef.current) return;
    try {
      const a = _ensureAudioEl();
      // 0.05s of silent mp3 — instantly fulfills the "play was triggered by user" rule.
      a.src = "data:audio/mpeg;base64,/+MYxAAAAANIAAAAAExBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV";
      const p = a.play();
      if (p && typeof p.then === "function") p.then(() => { audioUnlockedRef.current = true; }).catch(() => { /* ignore */ });
      // Also unlock SpeechSynthesis on Safari/iOS.
      if ("speechSynthesis" in window) {
        const u = new SpeechSynthesisUtterance("");
        window.speechSynthesis.speak(u);
      }
    } catch { /* ignore */ }
  };

  const speak = async (text) => {
    if (!voiceEnabled) return;
    const a = _ensureAudioEl();
    try { a.pause(); a.removeAttribute("src"); a.load(); } catch { /* ignore */ }

    // Track when Marcus is talking so the orb can pulse outward.
    setSpeaking(true);
    // We may queue multiple chunks — only run onEnd after the LAST chunk.
    const finalEnd = () => {
      setSpeaking(false);
      _scheduleAutoArm();
    };

    // FAST PATH — fire BOTH calls in parallel:
    //   1) /speak-chunks: returns just the FIRST sentence's audio (~800ms)
    //   2) /speak-rest:   returns the REMAINING sentences as one blob,
    //                     ready by the time the first sentence finishes playing.
    // Marcus starts speaking in ~0.8s instead of ~3.3s. The "rest" call
    // happens concurrently so we never wait sequentially.
    try {
      const firstPromise = api.post("/mock-interview/speak-chunks", { text, language });
      const r1 = await firstPromise;
      const firstUrl = r1.data?.first_chunk;
      const restText = r1.data?.rest_text || "";

      if (firstUrl) {
        // Kick off the rest-of-text TTS RIGHT NOW so it's loading while
        // sentence 1 plays. (Returns "" if there's nothing left.)
        const restPromise = restText
          ? api.post("/mock-interview/speak-rest", { text: restText, language })
          : Promise.resolve({ data: { data_url: "" } });

        // Play sentence 1
        a.src = firstUrl;
        _attachTTSAnalyser();
        a.onended = async () => {
          // Sentence 1 done — try to play the rest. By now restPromise
          // is almost certainly resolved.
          try {
            const r2 = await restPromise;
            const restUrl = r2.data?.data_url;
            if (restUrl) {
              a.src = restUrl;
              a.onended = finalEnd;
              a.onerror = finalEnd;
              try { await a.play(); }
              catch (err) { console.warn("rest chunk play blocked", err); finalEnd(); }
            } else {
              finalEnd();
            }
          } catch (err) {
            console.warn("rest chunk fetch failed", err);
            finalEnd();
          }
        };
        a.onerror = finalEnd;
        a.onpause = () => { /* ignore */ };
        try { await a.play(); return; }
        catch (playErr) {
          console.warn("audio.play() blocked — falling back to browser TTS", playErr);
        }
      }
    } catch (err) {
      console.warn("Fast TTS failed, falling back to single-blob TTS", err);
    }

    // Fallback 1: single-blob TTS endpoint (legacy).
    a.onended = finalEnd;
    a.onerror = finalEnd;
    a.onpause = () => { /* ignore */ };
    try {
      const r = await api.post("/mock-interview/speak", { text, language });
      const url = r.data?.data_url;
      if (url) {
        a.src = url;
        _attachTTSAnalyser();
        try { await a.play(); return; }
        catch (playErr) {
          console.warn("audio.play() blocked — falling back to browser TTS", playErr);
        }
      }
    } catch (err) {
      console.warn("Onyx TTS failed, falling back to browser voice", err);
    }

    // Fallback 2: browser SpeechSynthesis with locale-matched voice.
    if (!("speechSynthesis" in window)) { setSpeaking(false); return; }
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 0.95;
      u.pitch = 0.9;
      const langCode = ({ en: "en-US", ar: "ar-SA", de: "de-DE" })[language] || "en-US";
      u.lang = langCode;
      const voices = window.speechSynthesis.getVoices();
      const langMatches = voices.filter((v) => v.lang?.toLowerCase().startsWith(langCode.split("-")[0]));
      const deepMaleSameLang =
        langMatches.find((v) => /male|guy|eric|alex|daniel|markus|hans/i.test(v.name)) ||
        langMatches[0];
      if (deepMaleSameLang) u.voice = deepMaleSameLang;
      u.onend = finalEnd;
      u.onerror = finalEnd;
      window.speechSynthesis.speak(u);
    } catch (e) {
      console.warn("fallback speak failed", e);
      setSpeaking(false);
    }
  };

  const startWebcam = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: "user" },
        audio: false,
      });
      mediaStreamRef.current = stream;
      setWebcamOn(true);
    } catch (e) {
      console.warn("getUserMedia failed", e);
      toast.error("Camera access denied — you can still do voice-only interview");
    }
  };

  // Attach stream to <video> whenever the element is mounted AND webcam is on.
  // This runs AFTER React commits the DOM, which is the only reliable moment.
  useEffect(() => {
    if (!webcamOn) return;
    const el = videoRef.current;
    const stream = mediaStreamRef.current;
    if (!el || !stream) return;
    el.srcObject = stream;
    const tryPlay = () => el.play().catch((err) => console.warn("video.play()", err));
    if (el.readyState >= 2) {
      tryPlay();
    } else {
      el.onloadedmetadata = tryPlay;
    }
    return () => {
      el.onloadedmetadata = null;
    };
  }, [webcamOn]);

  const stopWebcam = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
    }
    if (detectIntervalRef.current) {
      clearInterval(detectIntervalRef.current);
      detectIntervalRef.current = null;
    }
    setWebcamOn(false);
    setEmotions(null);
  };

  // Start emotion detection loop once face-api is loaded AND video is ready
  useEffect(() => {
    if (!webcamOn || !faceLoaded || !videoRef.current) return;
    const faceapi = faceApiRef.current;
    if (!faceapi) return;
    const loop = async () => {
      try {
        if (!videoRef.current || videoRef.current.readyState < 2) return;
        const det = await faceapi
          .detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions())
          .withFaceExpressions();
        if (det && det.expressions) {
          const expr = det.expressions;
          setEmotions(expr);
          emotionsRollingRef.current.push({
            neutral: expr.neutral || 0,
            happy: expr.happy || 0,
            sad: expr.sad || 0,
            angry: expr.angry || 0,
            fearful: expr.fearful || 0,
            surprised: expr.surprised || 0,
            disgusted: expr.disgusted || 0,
          });
          if (emotionsRollingRef.current.length > 60) {
            emotionsRollingRef.current = emotionsRollingRef.current.slice(-60);
          }
        }
      } catch (err) {
        // silent
      }
    };
    detectIntervalRef.current = setInterval(loop, 800);
    return () => {
      if (detectIntervalRef.current) clearInterval(detectIntervalRef.current);
    };
  }, [webcamOn, faceLoaded]);

  const [specialty, setSpecialty] = useState("");
  const [specialtyOptions, setSpecialtyOptions] = useState([]);

  // Lazy-load specialty list (shown for DE + EN medical titles).
  useEffect(() => {
    let cancelled = false;
    const medicalContext = language === "de" || language === "en";
    if (medicalContext) {
      api.get("/mock-interview/specialties", { params: { language } })
        .then((r) => { if (!cancelled) setSpecialtyOptions(r.data?.items || []); })
        .catch(() => {});
    }
    return () => { cancelled = true; };
  }, [language]);

  const _MED_KW = /arzt|ärztin|assistenzarzt|facharzt|pflege|krankenpfleger|krankenschwester|mfa|hebamme|psychotherap|physiotherap|klinik|krankenhaus|praxis|apotheker|hospital|nurse|doctor|physician/i;
  const isMedicalJob = _MED_KW.test(jobTitle);

  const startInterview = async () => {
    // In Pro-Vertical modes the title is auto-set; only "chat" requires a title.
    if (mode === "chat" && !jobTitle.trim()) {
      toast.error("Enter a job title first");
      return;
    }
    // CRITICAL: unlock audio playback from this real user click — must happen
    // before any await so the browser keeps the gesture context.
    _unlockAudio();
    setThinking(true);
    try {
      const titleMap = {
        german_medical_case: "Klinischer Fall (DE)",
        english_medical_case: "Clinical Case (USMLE)",
        engineering_case: "Code Review",
        law_case: "Moot Court",
        finance_case: "Investor Pitch",
      };
      // The backend treats `english_medical_case` as a thin alias that
      // routes to the same OSCE-case engine with language=en.
      const backendMode = mode === "english_medical_case" ? "german_medical_case" : mode;
      const modeLang =
        mode === "german_medical_case" ? "de"
        : mode === "english_medical_case" ? "en"
        : mode === "chat" ? language
        : "en";
      const r = await api.post("/mock-interview/start", {
        job_title: titleMap[mode] || jobTitle.trim(),
        difficulty,
        language: modeLang,
        specialty: specialty || "",
        mode: backendMode,
        role_level: roleLevel,
        code_language: codeLanguage,
        law_wants_verdict: true,
      });
      setSession({ session_id: r.data.session_id, turn: r.data.turn, wrap_up: false });
      setMessages([{ role: "assistant", content: r.data.question }]);
      setResult(null);
      setPhase(r.data.phase || null);
      setPhaseLabel(r.data.phase_label || "");
      setVerticalCaseMeta({
        title: r.data.case_title || "",
        code: r.data.case_code || "",
        language: r.data.case_language || "",
        facts: r.data.case_facts || "",
        scenario: r.data.case_scenario || "",
        jurisdiction: r.data.case_jurisdiction || "",
      });
      // OSCE v2: store visual panels (vitals / EKG / labs / meds)
      setCasePanels(r.data.case_panels || null);
      setEcgLockedKey("");
      emotionsRollingRef.current = [];
      speak(r.data.question);
    } catch (err) {
      const code = err?.response?.data?.detail_obj?.code;
      if (code === "interview_quota_exceeded") {
        toast.error(err.response.data.detail);
      } else if (code === "medical_pro_required" || code === "pro_vertical_required") {
        setShowMedicalPaywall(true);
      } else {
        toast.error("Could not start interview");
      }
    } finally {
      setThinking(false);
    }
  };

  const currentFacialSummary = () => {
    const arr = emotionsRollingRef.current;
    if (arr.length === 0) return null;
    const keys = Object.keys(arr[0]);
    const avg = {};
    keys.forEach((k) => {
      avg[k] = arr.reduce((s, x) => s + (x[k] || 0), 0) / arr.length;
    });
    emotionsRollingRef.current = []; // reset window per-turn
    return avg;
  };

  const sendAnswer = async (answerText) => {
    // Always read the freshest session from the ref — closures captured by
    // MediaRecorder.onstop can be one render behind the actual state.
    const sess = sessionRef.current || session;
    if (!sess || !answerText) return;
    const facial = currentFacialSummary();
    setMessages((m) => [...m, { role: "user", content: answerText }]);
    setThinking(true);
    try {
      const r = await api.post("/mock-interview/reply", {
        session_id: sess.session_id,
        answer_text: answerText,
        facial_summary: facial,
      });
      setMessages((m) => [...m, { role: "assistant", content: r.data.question }]);
      setSession((s) => ({ ...(s || sess), turn: r.data.turn, wrap_up: r.data.wrap_up }));
      // German medical mode: track phase changes
      if (r.data.phase) {
        setPhase(r.data.phase);
        setPhaseLabel(r.data.phase_label || "");
      }
      if (r.data.preparation_seconds) {
        setPrepCountdown(r.data.preparation_seconds);
      }
      await speak(r.data.question);
      if (r.data.wrap_up) {
        // auto-finalize after 2s
        setTimeout(() => finalize(sess.session_id), 1800);
      }
      // (auto-rearm mic is handled by speak()'s onended → _scheduleAutoArm)
    } catch {
      toast.error("Could not send answer");
    } finally {
      setThinking(false);
    }
  };
  // Keep sendAnswer ref in sync so the prep-countdown timer can call it.
  useEffect(() => { sendAnswerRef.current = sendAnswer; });

  const silenceTimerRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const vadRafRef = useRef(null);

  const _cleanupVAD = () => {
    if (vadRafRef.current) { cancelAnimationFrame(vadRafRef.current); vadRafRef.current = null; }
    if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null; }
    try { analyserRef.current?.disconnect?.(); } catch { /* ignore */ }
    try { audioContextRef.current?.close?.(); } catch { /* ignore */ }
    analyserRef.current = null;
    audioContextRef.current = null;
  };

  const startRecording = async (e) => {
    try {
      e?.preventDefault?.();
      // Safety: stop any leftover recorder from a prior turn.
      try {
        if (recorderRef.current && recorderRef.current.state === "recording") {
          recorderRef.current.stop();
        }
      } catch { /* ignore */ }
      _cleanupVAD();

      if (!audioStreamRef.current || audioStreamRef.current.getTracks().every((t) => t.readyState !== "live")) {
        audioStreamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      }
      recordChunksRef.current = [];
      // Browser-specific codec selection: Safari only supports mp4/aac, Chrome/Firefox prefer webm/opus.
      let mimeType = "";
      const candidates = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4;codecs=mp4a.40.2",
        "audio/mp4",
        "audio/ogg;codecs=opus",
      ];
      for (const c of candidates) {
        if (window.MediaRecorder && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(c)) {
          mimeType = c;
          break;
        }
      }
      const mr = mimeType
        ? new MediaRecorder(audioStreamRef.current, { mimeType })
        : new MediaRecorder(audioStreamRef.current);
      const recordedType = mr.mimeType || mimeType || "audio/webm";
      mr.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) recordChunksRef.current.push(ev.data);
      };
      mr.onerror = (ev) => {
        console.warn("recorder error", ev);
        setRecording(false);
        _cleanupVAD();
      };
      mr.onstop = async () => {
        setRecording(false);
        _cleanupVAD();
        const blob = new Blob(recordChunksRef.current, { type: recordedType });
        recordChunksRef.current = [];
        recorderRef.current = null;
        if (blob.size < 800) {
          toast.error("Couldn't hear you — try again");
          return;
        }
        setTranscribing(true);
        try {
          const r = await api.post("/mock-interview/transcribe", blob, {
            headers: { "Content-Type": recordedType },
            transformRequest: [(data) => data],
            // Lock Whisper to the session language so it never drifts to
            // Arabic/Russian when the user is speaking German/English/Arabic.
            params: {
              session_id: session?.session_id,
              language: session?.language || language,
            },
          });
          const text = (r.data?.text || "").trim();
          if (!text) {
            toast.error("Couldn't catch that — try again");
            return;
          }
          await sendAnswer(text);
        } catch (err) {
          const detail = err?.response?.data?.detail || err?.message || "";
          toast.error(`Transcription failed${detail ? ` — ${String(detail).slice(0, 60)}` : ""}`);
        } finally {
          setTranscribing(false);
        }
      };
      mr.start();
      recorderRef.current = mr;
      setRecording(true);
      // Hard cap: even if VAD never detects silence (very loud room), stop after MAX_RECORD_MS.
      const hardStopTimer = setTimeout(() => {
        try { if (recorderRef.current?.state === "recording") recorderRef.current.stop(); } catch { /* ignore */ }
      }, 60000);
      // Make sure we clear it if recording stops normally first.
      const _origOnStop = mr.onstop;
      mr.onstop = (...args) => { clearTimeout(hardStopTimer); return _origOnStop?.(...args); };

      // ---- Voice Activity Detection (auto-stop on 1.5s silence) ----
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        const ctx = new AC();
        audioContextRef.current = ctx;
        const src = ctx.createMediaStreamSource(audioStreamRef.current);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        src.connect(analyser);
        analyserRef.current = analyser;
        const data = new Uint8Array(analyser.fftSize);
        const SILENCE_RMS = 0.020;       // quiet threshold (slightly above ambient noise floor)
        const SILENCE_MS = 1200;         // 1.2s of silence → auto-stop & auto-send
        const MIN_SPEAK_MS = 600;        // need at least 0.6s of speech before we'd auto-stop
        const MAX_RECORD_MS = 60000;     // hard cap: stop after 60s no matter what
        let speakingDetected = false;
        let speakingStartedAt = 0;

        const tick = () => {
          if (!recorderRef.current || recorderRef.current.state !== "recording") return;
          analyser.getByteTimeDomainData(data);
          let sum = 0;
          for (let i = 0; i < data.length; i++) {
            const v = (data[i] - 128) / 128;
            sum += v * v;
          }
          const rms = Math.sqrt(sum / data.length);

          if (rms > SILENCE_RMS) {
            // Speaking — clear any pending silence timer
            if (!speakingDetected) {
              speakingDetected = true;
              speakingStartedAt = Date.now();
            }
            if (silenceTimerRef.current) {
              clearTimeout(silenceTimerRef.current);
              silenceTimerRef.current = null;
            }
          } else if (speakingDetected && (Date.now() - speakingStartedAt) > MIN_SPEAK_MS && !silenceTimerRef.current) {
            // First moment of silence after real speech → start the auto-stop timer
            silenceTimerRef.current = setTimeout(() => {
              try { recorderRef.current?.stop(); } catch { /* ignore */ }
            }, SILENCE_MS);
          }
          vadRafRef.current = requestAnimationFrame(tick);
        };
        vadRafRef.current = requestAnimationFrame(tick);
      } catch (vadErr) {
        // VAD is best-effort; if AudioContext isn't supported the user can still tap to stop.
        console.warn("VAD unavailable", vadErr);
      }
    } catch (err) {
      console.warn("startRecording failed", err);
      toast.error("Microphone access denied");
      setRecording(false);
      _cleanupVAD();
    }
  };

  const stopRecording = (e) => {
    e?.preventDefault?.();
    try {
      const mr = recorderRef.current;
      if (mr && mr.state === "recording") {
        mr.stop();
      } else {
        setRecording(false);
      }
    } catch (err) {
      console.warn("stopRecording failed", err);
      setRecording(false);
    } finally {
      _cleanupVAD();
    }
  };

  const toggleRecording = (e) => {
    if (recording) stopRecording(e);
    else startRecording(e);
  };

  const finalize = async (sid) => {
    setThinking(true);
    setVoiceMode(false);  // turn voice mode off when results come in — show the report
    try {
      const r = await api.post(`/mock-interview/finalize/${sid || session.session_id}`);
      const res = r.data.result;
      setResult(res);

      // Build a spoken coaching summary in the same language Marcus used.
      const score = res.overall_score ?? 0;
      const strengths = (res.strengths || []).slice(0, 2).join(". ");
      const weaknesses = (res.weaknesses || []).slice(0, 2).join(". ");
      const advice = (res.advice || [])[0] || "";
      let narration = "";
      if (language === "de") {
        narration =
          `Vielen Dank für das Gespräch! Insgesamt geben wir Ihnen ${score} von 100 Punkten. ` +
          (strengths ? `Ihre Stärken: ${strengths}. ` : "") +
          (weaknesses ? `Worauf Sie achten sollten: ${weaknesses}. ` : "") +
          (advice ? `Mein wichtigster Tipp: ${advice}. ` : "") +
          "Schauen Sie sich die Reformulierungs-Vorschläge in Ruhe an — Sie schaffen das.";
      } else if (language === "ar") {
        narration =
          `شكراً على المقابلة! حصلت على ${score} من 100. ` +
          (strengths ? `أبرز نقاط القوة: ${strengths}. ` : "") +
          (weaknesses ? `أهم نقاط التحسين: ${weaknesses}. ` : "") +
          (advice ? `أهم نصيحة لي لك: ${advice}. ` : "") +
          "ألقِ نظرة على الاقتراحات لإعادة الصياغة — أداؤك ممتاز.";
      } else {
        narration =
          `Great job today — your overall score is ${score} out of 100. ` +
          (strengths ? `Your strengths: ${strengths}. ` : "") +
          (weaknesses ? `Things to work on: ${weaknesses}. ` : "") +
          (advice ? `My top piece of advice: ${advice}. ` : "") +
          "Take a look at the rephrasing suggestions below — you did really well.";
      }
      // Fire-and-forget: speak the coaching summary
      speak(narration);
    } catch {
      toast.error("Could not compute feedback");
    } finally {
      setThinking(false);
    }
  };

  const reset = () => {
    setSession(null);
    setMessages([]);
    setResult(null);
    setJobTitle("");
    emotionsRollingRef.current = [];
    api.get("/mock-interview/quota").then((r) => setQuota(r.data)).catch(() => {});
  };

  const topEmotion = emotions
    ? Object.entries(emotions).reduce((a, b) => (a[1] > b[1] ? a : b))
    : null;

  return (
    <div className="space-y-8" data-testid="mock-interview-page">
      {/* Medical Pro paywall modal */}
      {showMedicalPaywall && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setShowMedicalPaywall(false)}
          data-testid="mi-medical-paywall"
        >
          <div
            className="max-w-2xl w-full bg-white p-8 rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{ color: "#0a0a0a" }}
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] mb-3" style={{ color: "#92400e" }}>
              Locked feature · Choose your plan
            </div>
            <h2 className="font-black text-3xl tracking-tight mb-3" style={{ color: "#0a0a0a" }}>
              Unlock Medical OSCE Cases
            </h2>
            <p className="mb-6 leading-relaxed text-sm" style={{ color: "#374151" }}>
              Practice high-stakes clinical scenarios with our AI patient + senior consultant
              simulator. Built for IMGs preparing for the German Approbation Fachsprachenprüfung
              and KSA SCFHS exams. Two ways to unlock:
            </p>

            <div className="grid md:grid-cols-2 gap-4 mb-5">
              {/* Plan 1 — Medical OSCE only */}
              <div className="border-2 p-5 rounded-lg" style={{ background: "#fffbeb", borderColor: "#fde68a" }}>
                <div className="font-mono text-[10px] uppercase tracking-widest mb-2" style={{ color: "#92400e" }}>
                  Medical Pro
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-black tabular-nums" style={{ color: "#0a0a0a" }}>€150</span>
                  <span className="text-sm" style={{ color: "#6b7280" }}>/ month</span>
                </div>
                <div className="text-[11px] mt-1 font-mono" style={{ color: "#6b7280" }}>
                  ≈ 605 SAR · ≈ $158 USD
                </div>
                <ul className="text-xs mt-4 space-y-1.5" style={{ color: "#374151" }}>
                  <li>🏥 Unlimited Medical OSCE cases</li>
                  <li>👨‍⚕️ AI patient · simulated Anamnese</li>
                  <li>🩺 Chefarzt grilling phase</li>
                  <li>📋 Detailed coaching report per case</li>
                </ul>
              </div>

              {/* Plan 2 — Bundle (better value) */}
              <div className="border-2 p-5 rounded-lg relative" style={{ background: "#0a0a0a", borderColor: "#0a0a0a", color: "#fff" }}>
                <div className="absolute -top-2 right-3 bg-[#FF3B30] text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded">
                  Best value
                </div>
                <div className="font-mono text-[10px] uppercase tracking-widest mb-2" style={{ color: "#fcd34d" }}>
                  Medical Bundle
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-black tabular-nums">€270</span>
                  <span className="text-sm text-white/70">/ month</span>
                </div>
                <div className="text-[11px] mt-1 font-mono text-white/60">
                  ≈ 1,090 SAR · ≈ $284 USD
                </div>
                <ul className="text-xs mt-4 space-y-1.5 text-white/90">
                  <li>🎤 <b>50</b> Marcus interviews / month</li>
                  <li>🏥 <b>20</b> Medical OSCE cases / month</li>
                  <li>👨‍⚕️ AI patient + Chefarzt</li>
                  <li>📋 Coaching reports + history</li>
                  <li className="text-[#fcd34d]">⭐ Save €30 vs. buying separately</li>
                </ul>
              </div>
            </div>

            <button
              disabled
              className="w-full py-4 font-bold text-sm cursor-not-allowed rounded-lg"
              style={{ background: "#e5e7eb", color: "#9ca3af" }}
              data-testid="mi-medical-pay-btn"
            >
              Soon available — payment integration coming
            </button>
            <p className="text-[11px] text-center mt-3" style={{ color: "#6b7280" }}>
              We're finalizing our Stripe / Moyasar setup. Drop us a line if you'd like early access.
            </p>
            <button
              onClick={() => setShowMedicalPaywall(false)}
              className="block mx-auto mt-4 text-sm underline"
              style={{ color: "#6b7280" }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">
            / AI Mock Interview · Marcus
          </div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">
            Practice with Marcus.
            <br />
            <span className="text-gradient">Real voice. Real feedback.</span>
          </h1>
          <p className="text-gray-600 mt-3 max-w-2xl">
            A warm AI recruiter who asks real interview questions, listens to your answers,
            reads your facial expressions — and gives you a full score &amp; coaching report when you're done.
          </p>
        </div>
        {quota && (
          <div className="border border-black/10 bg-[#F8F9FA] px-5 py-3 text-sm">
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
              Your quota
            </div>
            <div className="font-display font-bold text-lg">
              {quota.unlimited
                ? "Unlimited (Pro+)"
                : `${quota.remaining} / ${quota.limit} free left this month`}
            </div>
          </div>
        )}
      </div>

      {quota?.locked ? (
        // Free tier — locked paywall
        <div className="border border-black/10 bg-[#F8F9FA] p-8 md:p-12 max-w-2xl" data-testid="mi-paywall">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#FF3B30] text-white flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF3B30]">
              Paid feature
            </div>
          </div>
          <h2 className="font-display font-black text-3xl md:text-4xl tracking-tighter mb-3">
            Unlock Marcus — your AI interview coach.
          </h2>
          <p className="text-gray-700 mb-6">
            Full voice practice with real Onyx-grade speech, facial expression analysis, and Claude-powered coaching feedback. Available on every paid plan.
          </p>
          <ul className="space-y-2 text-sm mb-8">
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 mt-2 bg-[#0033FF] flex-shrink-0" />
              <span><strong>Basic (SAR 249/mo)</strong> — 2 interviews/month</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 mt-2 bg-[#0033FF] flex-shrink-0" />
              <span><strong>Pro (SAR 360/mo)</strong> — unlimited interviews</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 mt-2 bg-[#0033FF] flex-shrink-0" />
              <span><strong>Agency</strong> — unlimited + multi-seat</span>
            </li>
          </ul>
          <a
            href="/app/billing"
            className="bg-black text-white hover:bg-[#0033FF] font-bold px-8 py-4 inline-flex items-center gap-3 transition-colors"
            data-testid="mi-upgrade-cta"
          >
            <Trophy className="w-5 h-5" /> Upgrade to unlock Marcus
          </a>
        </div>
      ) : !session && !result ? (
        // Setup screen
        showDashboard ? (
          <Suspense fallback={
            <div className="flex items-center justify-center py-20 text-white/50">
              <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
            </div>
          }>
            <div className="bg-black text-white p-6 -mx-6 sm:mx-0 sm:p-8">
              <LearningDashboard onBack={() => setShowDashboard(false)} />
            </div>
          </Suspense>
        ) : (
        <div className="grid md:grid-cols-2 gap-6">
          <div className="border border-black/10 bg-white p-8" data-testid="mi-setup">
            {/* My Progress quick link — prominent History entry */}
            <button
              onClick={() => setShowDashboard(true)}
              className="mb-4 w-full text-sm font-bold uppercase tracking-wide text-white bg-black hover:bg-[#0033FF] px-4 py-3 inline-flex items-center justify-center gap-2 transition-colors"
              data-testid="mi-open-dashboard"
            >
              📜 View My Interview History &amp; OSCE Mastery →
            </button>
            {/* Mode picker — Standard + 4 Pro-Vertical modes */}
            <div className="mb-6 -mx-2">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-2 px-2">
                Interview Mode
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  onClick={() => setMode("chat")}
                  className={`px-3 py-2.5 text-sm font-bold border transition-colors ${
                    mode === "chat" ? "bg-black text-white border-black" : "border-black/10 hover:bg-black/5"
                  }`}
                  data-testid="mi-mode-chat"
                >
                  Standard (Marcus)
                </button>
                {[
                  { id: "german_medical_case", label: "Medical Case (DE)", testid: "mi-mode-medical" },
                  { id: "english_medical_case", label: "Medical Case · USMLE (EN)", testid: "mi-mode-medical-en" },
                  { id: "engineering_case", label: "Engineering · Code Review", testid: "mi-mode-engineering" },
                  { id: "law_case", label: "Law · Moot Court", testid: "mi-mode-law" },
                  { id: "finance_case", label: "Finance · Investor Pitch", testid: "mi-mode-finance" },
                ].map((o) => {
                  const locked = !hasProVertical(user);
                  const active = mode === o.id;
                  return (
                    <button
                      key={o.id}
                      onClick={() => locked ? setShowMedicalPaywall(true) : setMode(o.id)}
                      className={`relative px-3 py-2.5 text-sm font-bold border transition-colors ${
                        active
                          ? "bg-black text-white border-black"
                          : locked
                            ? "border-amber-500/40 bg-amber-50 text-amber-900 hover:bg-amber-100"
                            : "border-black/10 hover:bg-black/5"
                      }`}
                      data-testid={o.testid}
                    >
                      <span className="flex items-center justify-center gap-1.5">
                        {locked && <span>🔒</span>}
                        {o.label}
                        {locked && (
                          <span className="text-[9px] font-mono uppercase tracking-wider opacity-80">
                            €150/mo
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
              {mode === "german_medical_case" && hasProVertical(user) && (
                <p className="text-xs text-gray-600 mt-3 leading-relaxed">
                  Phase 1: Anamnesis with the patient (12 Qs). Nurse delivers vitals after 5.
                  Phase 2: 5-min preparation. Phase 3: Case presentation to the senior consultant.
                </p>
              )}
              {mode === "english_medical_case" && hasProVertical(user) && (
                <p className="text-xs text-gray-600 mt-3 leading-relaxed">
                  USMLE-style clinical case in English. Phase 1: History-taking with the standardized
                  patient (12 Qs). Phase 2: 5-min preparation. Phase 3: Oral case presentation to the
                  attending physician. Full OSCE workflow in English.
                </p>
              )}
              {mode === "engineering_case" && hasProVertical(user) && (
                <p className="text-xs text-gray-600 mt-3 leading-relaxed">
                  A senior engineer shows you a real code snippet with bugs. Defend / fix it live.
                  Covers race conditions, N+1, memory leaks, security, architecture.
                </p>
              )}
              {mode === "law_case" && hasProVertical(user) && (
                <p className="text-xs text-gray-600 mt-3 leading-relaxed">
                  You're counsel in a moot-court hearing. Argue the case before an AI judge who grills
                  you on jurisdiction, statute, and precedent. Say "Please rule, Your Honor" anytime
                  to receive a reasoned verdict.
                </p>
              )}
              {mode === "finance_case" && hasProVertical(user) && (
                <p className="text-xs text-gray-600 mt-3 leading-relaxed">
                  Pitch a valuation or M&amp;A deal to a sceptical investment-committee chair.
                  Expect grilling on DCF, multiples, sensitivities, and dilution.
                </p>
              )}
            </div>

            {(mode === "german_medical_case" || mode === "english_medical_case") ? (
              <label className="block mb-5">
                <span className="block font-mono text-xs uppercase tracking-widest text-gray-500 mb-2">
                  {mode === "german_medical_case" ? "Ihre Rolle" : "Your role"}
                </span>
                <div className="flex gap-2">
                  {(mode === "german_medical_case"
                    ? [
                        { id: "assistenzarzt", label: "Assistenzarzt" },
                        { id: "facharzt", label: "Facharzt" },
                      ]
                    : [
                        { id: "assistenzarzt", label: "Resident (PGY-1/2)" },
                        { id: "facharzt", label: "Attending" },
                      ]
                  ).map((r) => (
                    <button
                      key={r.id}
                      onClick={() => setRoleLevel(r.id)}
                      className={`px-5 py-2 font-bold text-sm transition-colors ${
                        roleLevel === r.id
                          ? "bg-black text-white"
                          : "border border-black/10 hover:bg-black/5"
                      }`}
                      data-testid={`mi-role-${r.id}`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {mode === "german_medical_case"
                    ? "Der Chefarzt passt sein Niveau an: Assistenzarzt = lehrreich-anspruchsvoll, Facharzt = sokratisch-streng."
                    : "The attending scales the difficulty: Resident = teaching tone, Attending = Socratic-strict (board-exam level)."}
                </p>
              </label>
            ) : mode === "engineering_case" ? (
              <label className="block mb-5">
                <span className="block font-mono text-xs uppercase tracking-widest text-gray-500 mb-2">
                  Code language
                </span>
                <div className="flex flex-wrap gap-2">
                  {[
                    { id: "python", label: "Python" },
                    { id: "javascript", label: "JavaScript" },
                    { id: "java", label: "Java" },
                    { id: "cpp", label: "C++" },
                    { id: "sql", label: "SQL" },
                    { id: "go", label: "Go" },
                  ].map((r) => (
                    <button
                      key={r.id}
                      onClick={() => setCodeLanguage(r.id)}
                      className={`px-4 py-2 font-bold text-sm transition-colors ${
                        codeLanguage === r.id
                          ? "bg-black text-white"
                          : "border border-black/10 hover:bg-black/5"
                      }`}
                      data-testid={`mi-codelang-${r.id}`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  The senior engineer will pull up a snippet in the language you pick.
                </p>
              </label>
            ) : (mode === "law_case" || mode === "finance_case") ? (
              <div className="block mb-5 text-xs text-gray-500 leading-relaxed">
                A random case will be generated when you begin the session.
              </div>
            ) : (
              <label className="block mb-5">
                <span className="block font-mono text-xs uppercase tracking-widest text-gray-500 mb-2">
                  Job title
                </span>
                <input
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  placeholder="e.g. Senior Backend Engineer"
                  className="w-full bg-transparent border-b-2 border-gray-200 focus:border-black outline-none py-3 text-lg"
                  data-testid="mi-title"
                />
              </label>
            )}
            <label className="block mb-5">
              <span className="block font-mono text-xs uppercase tracking-widest text-gray-500 mb-2">
                Difficulty
              </span>
              <div className="flex gap-2">
                {[
                  { id: "friendly", label: "Friendly" },
                  { id: "strict", label: "Strict" },
                ].map((d) => (
                  <button
                    key={d.id}
                    onClick={() => setDifficulty(d.id)}
                    className={`px-5 py-2 font-bold text-sm transition-colors ${
                      difficulty === d.id
                        ? "bg-black text-white"
                        : "border border-black/10 hover:bg-black/5"
                    }`}
                    data-testid={`mi-diff-${d.id}`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </label>
            <label className="block mb-5">
              <span className="block font-mono text-xs uppercase tracking-widest text-gray-500 mb-2">
                Interview language
              </span>
              <div className="flex gap-2 flex-wrap">
                {[
                  { id: "en", label: "English", flag: "🇬🇧" },
                  { id: "ar", label: "العربية", flag: "🇸🇦" },
                  { id: "de", label: "Deutsch (B2)", flag: "🇩🇪" },
                ].map((l) => (
                  <button
                    key={l.id}
                    onClick={() => setLanguage(l.id)}
                    className={`px-5 py-2 font-bold text-sm transition-colors flex items-center gap-2 ${
                      language === l.id
                        ? "bg-black text-white"
                        : "border border-black/10 hover:bg-black/5"
                    }`}
                    data-testid={`mi-lang-${l.id}`}
                  >
                    <span>{l.flag}</span> {l.label}
                  </button>
                ))}
              </div>
              {language === "de" && (
                <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                  Marcus stellt seine Fragen auf Deutsch (B2-Niveau). Am Ende bekommst du
                  konkrete Reformulierungs-Vorschläge auf Deutsch — inklusive STAR-Struktur.
                </p>
              )}
            </label>

            {/* Medical specialty picker: shown for DE + EN when the title looks medical */}
            {(language === "de" || language === "en") && isMedicalJob && specialtyOptions.length > 0 && (
              <label className="block mb-5" data-testid="mi-specialty-block">
                <span className="block font-mono text-xs uppercase tracking-widest text-gray-500 mb-2">
                  {language === "de"
                    ? <>Welche Abteilung? <span className="text-gray-400 normal-case">(empfohlen für Assistenzarzt-Bewerbungen)</span></>
                    : <>Which specialty? <span className="text-gray-400 normal-case">(recommended for residency prep)</span></>}
                </span>
                <select
                  value={specialty}
                  onChange={(e) => setSpecialty(e.target.value)}
                  className="w-full bg-white border border-black/15 px-4 py-3 text-sm font-medium focus:border-black focus:outline-none rounded"
                  data-testid="mi-specialty-select"
                >
                  <option value="">
                    {language === "de" ? "— Allgemein, ohne Schwerpunkt —" : "— General, no specialty —"}
                  </option>
                  {specialtyOptions.map((s) => (
                    <option key={s.key} value={s.key}>{s.name}</option>
                  ))}
                </select>
                {specialty && language === "de" && (
                  <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                    Marcus startet mit Hintergrundfragen (seit wann in Deutschland, Approbation,
                    Sprachniveau, Famulatur in {specialtyOptions.find((s) => s.key === specialty)?.name})
                    und stellt dann fachspezifische klinische Fragen.
                  </p>
                )}
                {specialty && language === "en" && (
                  <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                    Marcus will run a structured interview focused on {specialtyOptions.find((s) => s.key === specialty)?.name} —
                    background, clinical reasoning, and USMLE-style scenarios.
                  </p>
                )}
              </label>
            )}
            <div className="flex items-center gap-3 mb-6">
              <button
                onClick={voiceEnabled ? () => setVoiceEnabled(false) : () => setVoiceEnabled(true)}
                className="p-2 border border-black/10 hover:bg-black/5"
                title={voiceEnabled ? "Mute Marcus" : "Unmute Marcus"}
                data-testid="mi-voice-toggle"
              >
                {voiceEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </button>
              <button
                onClick={webcamOn ? stopWebcam : startWebcam}
                className="p-2 border border-black/10 hover:bg-black/5 flex items-center gap-2"
                title={webcamOn ? "Stop webcam" : "Start webcam for facial analysis"}
                data-testid="mi-webcam-toggle"
              >
                {webcamOn ? <VideoOff className="w-4 h-4" /> : <Video className="w-4 h-4" />}
                <span className="text-xs font-mono uppercase tracking-widest">
                  {webcamOn ? "Webcam on" : "Enable webcam"}
                </span>
              </button>
            </div>
            <button
              onClick={startInterview}
              disabled={thinking || (mode === "chat" && !jobTitle.trim())}
              className="w-full bg-black text-white hover:bg-[#0033FF] font-bold py-4 flex items-center justify-center gap-3 transition-colors disabled:opacity-60"
              data-testid="mi-start"
            >
              <Sparkles className="w-5 h-5" />
              {thinking ? "Waking Marcus up…" : (
                mode === "german_medical_case" ? "Start Medical OSCE Case" :
                mode === "english_medical_case" ? "Start USMLE Clinical Case" :
                mode === "engineering_case" ? "Start Code Review" :
                mode === "law_case" ? "Start Moot Court" :
                mode === "finance_case" ? "Start Investor Pitch" :
                "Start interview with Marcus"
              )}
            </button>
          </div>

          <div className="border border-black/10 bg-[#F8F9FA] p-8 flex flex-col items-center justify-center text-center">
            {portrait ? (
              <img
                src={portrait}
                alt="Marcus — your AI interviewer"
                className="w-48 h-48 rounded-full object-cover border-4 border-white shadow-xl mb-5"
                data-testid="mi-portrait"
              />
            ) : (
              <div className="w-48 h-48 rounded-full bg-gradient-to-br from-[#0033FF] to-[#FF3B30] flex items-center justify-center mb-5">
                <UserIcon className="w-16 h-16 text-white" />
              </div>
            )}
            <div className="font-display font-black text-3xl tracking-tighter">Marcus</div>
            <div className="font-mono text-xs uppercase tracking-widest text-gray-500 mt-1">
              Senior AI recruiter
            </div>
            <p className="text-sm text-gray-600 mt-4 max-w-xs">
              {mode === "chat"
                ? "Enter the job title you're practicing for. Marcus will run a 15-question interview, then give you a full coaching report."
                : mode === "german_medical_case"
                  ? "3-phase OSCE simulator: Anamnese with the patient, then case presentation to the Chefarzt. Ready when you are."
                  : mode === "english_medical_case"
                    ? "3-phase USMLE-style simulator: history-taking with a standardized patient, then oral case presentation to the attending. Ready when you are."
                    : "Specialized vertical simulator. Ready when you are — no job title needed."}
            </p>
          </div>
        </div>
        )
      ) : session && !result ? (
        <>
          {/* ChatGPT-style Voice Mode overlay — fullscreen pulsing orb */}
          {voiceMode && (
            <div
              className="fixed inset-0 z-50 bg-[#0a0a0c] flex flex-col items-center justify-center"
              data-testid="mi-voice-mode"
            >
              {/* Top bar */}
              <div className="absolute top-0 inset-x-0 px-6 py-5 flex items-center justify-between text-white">
                <div className="flex items-center gap-3">
                  {portrait && <img src={portrait} alt="" className="w-8 h-8 rounded-full" />}
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">
                    Marcus · {language === "de" ? "Deutsch" : language === "ar" ? "العربية" : "English"}
                    {session.field === "medical" && specialty && ` · ${specialtyOptions.find(s => s.key === specialty)?.name || ""}`}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => finalize(session.session_id)}
                    className="bg-white text-black hover:bg-white/90 font-bold px-4 py-1.5 text-xs uppercase tracking-widest rounded-full"
                    data-testid="mi-voice-end"
                  >
                    End interview
                  </button>
                </div>
              </div>

              {/* OSCE Visual Panels overlay — shown during voice mode for medical cases */}
              {(mode === "german_medical_case" || mode === "english_medical_case") && casePanels && (
                <div
                  className="absolute right-4 top-20 bottom-20 w-[min(40vw,440px)] bg-black/70 backdrop-blur-md border border-white/10 rounded-2xl overflow-y-auto p-4 z-10"
                  data-testid="mi-medical-panels-overlay"
                >
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 mb-3">
                    Patient documents
                  </div>
                  <Suspense fallback={null}>
                    <OscePanels
                      panels={casePanels}
                      phase={phase}
                      ecgLockedKey={ecgLockedKey}
                      ecgLocked={!!ecgLockedKey}
                      onEcgAnswer={async (key) => {
                        const r = await api.post("/mock-interview/ecg-answer", {
                          session_id: session.session_id,
                          ecg_answer_key: key,
                        });
                        setEcgLockedKey(key);
                        return r.data;
                      }}
                    />
                  </Suspense>
                </div>
              )}

              {/* Case content side-panel — Code / Facts / Scenario depending on mode */}
              {(mode === "engineering_case" || mode === "law_case" || mode === "finance_case") && (
                <div
                  className="absolute left-4 top-20 bottom-20 w-[min(42vw,520px)] bg-black/60 backdrop-blur-md border border-white/10 rounded-2xl overflow-hidden flex flex-col"
                  data-testid="mi-case-panel"
                >                  <div className="px-4 py-2.5 border-b border-white/10 flex items-center justify-between">
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">
                      {mode === "engineering_case" && "Code under review"}
                      {mode === "law_case" && "Case facts"}
                      {mode === "finance_case" && "Deal scenario"}
                    </div>
                    {verticalCaseMeta?.title && (
                      <div className="text-[11px] text-white/40 truncate ml-2">
                        {verticalCaseMeta.title}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 overflow-auto p-4">
                    {mode === "engineering_case" && verticalCaseMeta?.code && (
                      <pre className="text-xs font-mono text-emerald-300 whitespace-pre-wrap leading-relaxed">
{verticalCaseMeta.code}
                      </pre>
                    )}
                    {mode === "law_case" && verticalCaseMeta?.facts && (
                      <div className="text-sm text-white/85 whitespace-pre-wrap leading-relaxed">
                        {verticalCaseMeta.facts}
                        {verticalCaseMeta.jurisdiction && (
                          <div className="mt-4 text-[11px] font-mono uppercase tracking-widest text-amber-300/80">
                            Jurisdiction focus: {verticalCaseMeta.jurisdiction}
                          </div>
                        )}
                      </div>
                    )}
                    {mode === "finance_case" && verticalCaseMeta?.scenario && (
                      <div className="text-sm text-white/85 whitespace-pre-wrap leading-relaxed">
                        {verticalCaseMeta.scenario}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* The 3D robot — Spline scene, lip-syncs to Marcus's voice via Web Audio analyser */}
              <div className="flex-1 flex items-center justify-center w-full relative">
                <div
                  ref={splineWrapRef}
                  className="w-[min(80vw,640px)] h-[min(80vw,640px)] max-h-[70vh] transition-[transform,filter] duration-100 ease-out"
                  style={{ filter: "drop-shadow(0 0 18px rgba(91,140,255,0.25))" }}
                  data-testid="mi-robot"
                >
                  <Suspense fallback={
                    <div className="w-full h-full flex items-center justify-center text-white/40">
                      <Loader2 className="w-8 h-8 animate-spin" />
                    </div>
                  }>
                    <Spline scene={SPLINE_ROBOT_SCENE} onLoad={_onSplineLoad} />
                  </Suspense>
                </div>
                {/* Invisible click target so tap-to-speak still works on top of the 3D scene */}
                <button
                  onClick={toggleRecording}
                  disabled={thinking || transcribing || speaking}
                  className="absolute inset-0 m-auto w-56 h-56 rounded-full focus:outline-none cursor-pointer bg-transparent"
                  data-testid="mi-orb"
                  aria-label={recording ? "Listening — auto-stops on silence" : speaking ? "Marcus speaking" : "Idle"}
                >
                  {/* Idle state — waiting for the next event (Marcus or the auto-rearm useEffect) */}
                  {!recording && !speaking && !thinking && !transcribing && (
                    <span className="absolute inset-0 flex items-end justify-center pb-4 text-white/70">
                      <span className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.25em] bg-black/40 backdrop-blur px-3 py-1.5 rounded-full">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Connecting…
                      </span>
                    </span>
                  )}
                  {recording && (
                    <span className="absolute inset-0 flex items-end justify-center pb-4 text-white">
                      <span className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.25em] bg-emerald-600/80 backdrop-blur px-3 py-1.5 rounded-full animate-pulse">
                        <Mic className="w-3.5 h-3.5" /> Listening — just talk
                      </span>
                    </span>
                  )}
                  {(thinking || transcribing) && (
                    <span className="absolute inset-0 flex items-end justify-center pb-4 text-white/70">
                      <span className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.25em] bg-black/40 backdrop-blur px-3 py-1.5 rounded-full">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        {transcribing ? "Transcribing…" : "Thinking…"}
                      </span>
                    </span>
                  )}
                </button>
              </div>

              {/* Status text */}
              <div className="pb-12 text-center px-6">
                <div className="text-white/95 text-lg font-medium mb-1 min-h-[28px]" data-testid="mi-voice-status">
                  {recording
                    ? (language === "de" ? "Ich höre zu… (sprechen Sie, ich erkenne Pausen automatisch)"
                       : language === "ar" ? "أنا أستمع… (تحدث، سأتعرف على الصمت تلقائياً)"
                       : "Listening… (just talk, I auto-detect when you stop)")
                    : speaking
                      ? (language === "de" ? "Marcus spricht…" : language === "ar" ? "ماركوس يتكلم…" : "Marcus is speaking…")
                      : thinking
                        ? (language === "de" ? "Marcus überlegt…" : language === "ar" ? "ماركوس يفكر…" : "Marcus is thinking…")
                        : transcribing
                          ? (language === "de" ? "Wird transkribiert…" : language === "ar" ? "يتم النسخ…" : "Transcribing…")
                          : (language === "de" ? "Einen Moment…"
                             : language === "ar" ? "لحظة…"
                             : "One moment…")}
                </div>
                <div className="text-white/40 text-xs font-mono uppercase tracking-widest mt-2">
                  {mode === "german_medical_case" && phaseLabel
                    ? phaseLabel
                    : `Turn ${session.turn || 1} / 8`}
                </div>

                {/* German Medical mode — preparation countdown */}
                {mode === "german_medical_case" && phase === "preparation" && prepCountdown > 0 && (
                  <div
                    className="mt-6 px-6 py-5 border border-amber-400/40 bg-amber-500/10 backdrop-blur rounded-2xl max-w-xl mx-auto text-amber-100"
                    data-testid="mi-prep-countdown"
                  >
                    <div className="text-xs font-mono uppercase tracking-[0.25em] mb-2">
                      5 Min Vorbereitung
                    </div>
                    <div className="text-5xl font-mono font-bold tabular-nums">
                      {String(Math.floor(prepCountdown / 60)).padStart(2, "0")}:
                      {String(prepCountdown % 60).padStart(2, "0")}
                    </div>
                    <div className="text-xs text-amber-200/80 mt-2 leading-relaxed">
                      Notieren Sie Ihre Verdachtsdiagnose, Differenzialdiagnosen, Diagnostik und Therapieplan.
                      Der Chefarzt erscheint automatisch nach Ablauf des Timers.
                    </div>
                    <button
                      onClick={() => setPrepCountdown(0)}
                      className="mt-3 text-xs font-mono uppercase tracking-widest px-3 py-1.5 border border-amber-300/40 hover:bg-amber-400/20 rounded-full"
                      data-testid="mi-prep-skip"
                    >
                      Direkt zum Chefarzt →
                    </button>
                  </div>
                )}

                <button
                  onClick={() => finalize(session.session_id)}
                  className="mt-8 text-xs font-mono uppercase tracking-widest text-white/50 hover:text-white px-4 py-2 border border-white/10 rounded-full"
                  data-testid="mi-voice-end"
                >
                  End interview & get feedback
                </button>
              </div>
            </div>
          )}
          {/* Live interview */}
        <div className="grid lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] gap-6">
          {/* Chat */}
          <div className="border border-black/10 bg-white flex flex-col h-[75vh]" data-testid="mi-chat">
            <div className="px-6 py-4 border-b border-black/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {portrait ? (
                  <img src={portrait} alt="Marcus" className="w-9 h-9 rounded-full object-cover" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#0033FF] to-[#FF3B30]" />
                )}
                <div>
                  <div className="font-display font-bold">Marcus</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
                    Turn {session.turn} · {jobTitle}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setVoiceEnabled((v) => !v)}
                  className="p-2 hover:bg-black/5"
                  data-testid="mi-voice-toggle-live"
                >
                  {voiceEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => finalize(session.session_id)}
                  className="text-xs font-bold px-3 py-1.5 bg-[#FF3B30] text-white hover:bg-black transition-colors"
                  data-testid="mi-finish"
                >
                  Finish now
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5 bg-[#FAFAFA]">
              {messages.map((m, i) => (
                <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                  <div className="w-9 h-9 flex-shrink-0 rounded-full overflow-hidden border border-black/10 bg-white flex items-center justify-center">
                    {m.role === "user" ? (
                      <UserIcon className="w-4 h-4" />
                    ) : portrait ? (
                      <img src={portrait} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Sparkles className="w-4 h-4 text-[#0033FF]" />
                    )}
                  </div>
                  <div
                    className={`max-w-[78%] px-5 py-3.5 text-[15px] leading-relaxed rounded-2xl ${
                      m.role === "user"
                        ? "bg-[#0033FF] text-white rounded-tr-sm"
                        : "bg-white border border-black/5 rounded-tl-sm shadow-sm"
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {(thinking || transcribing) && (
                <div className="flex gap-3">
                  <div className="w-9 h-9 flex-shrink-0 rounded-full bg-white border border-black/10" />
                  <div className="px-5 py-3.5 bg-white rounded-2xl border border-black/5 font-mono text-sm text-gray-500 flex items-center gap-1.5">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0.15s" }} />
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0.3s" }} />
                    {transcribing && <span className="ml-2">transcribing…</span>}
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>
            <div className="border-t border-black/10 p-4 bg-white">
              {!typing ? (
                <div className="flex items-center gap-3">
                  <button
                    onClick={toggleRecording}
                    disabled={thinking || transcribing}
                    className={`flex-1 py-4 font-bold rounded-full flex items-center justify-center gap-3 transition-all disabled:opacity-50 ${
                      recording
                        ? "bg-white text-[#1a1a1c] ring-2 ring-white/40 animate-pulse"
                        : "bg-white text-[#1a1a1c] hover:bg-white/90"
                    }`}
                    data-testid="mi-record"
                    title={recording ? "Click to stop (or just stop talking — auto-sends after 1.5s of silence)" : "Click to start speaking"}
                  >
                    {recording ? (
                      <>
                        <Square className="w-5 h-5" /> Listening… tap to stop
                      </>
                    ) : (
                      <>
                        <Mic className="w-5 h-5" /> Tap to speak
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setTyping(true)}
                    className="px-4 py-4 border border-black/10 hover:bg-black/5 text-sm font-bold"
                    title="Type instead"
                    data-testid="mi-type-toggle"
                  >
                    Type
                  </button>
                </div>
              ) : (
                <form onSubmit={submitTyped} className="flex items-end gap-2">
                  <textarea
                    value={typedAnswer}
                    onChange={(e) => setTypedAnswer(e.target.value)}
                    placeholder="Type your answer…"
                    rows={2}
                    className="flex-1 border border-black/10 focus:border-black outline-none px-4 py-3 text-sm resize-none"
                    autoFocus
                    data-testid="mi-type-input"
                  />
                  <button
                    type="submit"
                    disabled={thinking || !typedAnswer.trim()}
                    className="bg-black text-white hover:bg-[#0033FF] font-bold px-5 py-3 disabled:opacity-40 text-sm"
                    data-testid="mi-type-send"
                  >
                    Send
                  </button>
                  <button
                    type="button"
                    onClick={() => { setTyping(false); setTypedAnswer(""); }}
                    className="px-3 py-3 text-sm hover:bg-black/5"
                  >
                    Voice
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Webcam + emotion panel + OSCE panels */}
          <div className="space-y-4">
            {/* OSCE Visual Panels (vitals, EKG, labs, meds) — shown for both DE + EN medical case modes */}
            {(mode === "german_medical_case" || mode === "english_medical_case") && casePanels && (
              <Suspense fallback={null}>
                <OscePanels
                  panels={casePanels}
                  phase={phase}
                  ecgLockedKey={ecgLockedKey}
                  ecgLocked={!!ecgLockedKey}
                  onEcgAnswer={async (key) => {
                    const r = await api.post("/mock-interview/ecg-answer", {
                      session_id: session.session_id,
                      ecg_answer_key: key,
                    });
                    setEcgLockedKey(key);
                    return r.data;
                  }}
                />
              </Suspense>
            )}
            <div className="border border-black/10 bg-black aspect-video relative overflow-hidden" data-testid="mi-webcam">
              {/* Video element stays mounted so ref is always available — we just hide it when webcam off */}
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                className={`w-full h-full object-cover ${webcamOn ? "" : "hidden"}`}
              />
              {!webcamOn && (
                <div className="h-full flex flex-col items-center justify-center text-white/70 p-6 text-center">
                  <VideoOff className="w-10 h-10 mb-3 stroke-[1]" />
                  <div className="text-sm mb-4">Webcam off — facial analysis paused</div>
                  <button
                    onClick={startWebcam}
                    className="bg-white text-black font-bold px-5 py-2 text-sm hover:bg-[#FF3B30] hover:text-white transition-colors"
                    data-testid="mi-enable-webcam"
                  >
                    Enable webcam
                  </button>
                </div>
              )}
              {webcamOn && topEmotion && (
                <div className="absolute bottom-3 left-3 right-3 bg-black/70 backdrop-blur px-3 py-2 text-white">
                  <div className="flex items-center justify-between text-xs font-mono uppercase tracking-widest">
                    <span className="opacity-70">Current</span>
                    <span className="font-bold">
                      {topEmotion[0]} · {Math.round(topEmotion[1] * 100)}%
                    </span>
                  </div>
                </div>
              )}
              {webcamOn && !faceLoaded && !faceError && (
                <div className="absolute top-3 left-3 bg-black/70 text-white text-xs px-2 py-1 font-mono">
                  Loading face model…
                </div>
              )}
              {faceError && (
                <div className="absolute top-3 left-3 right-3 bg-[#FF3B30]/90 text-white text-xs p-2 font-mono">
                  Face model couldn't load — voice interview still works.
                </div>
              )}
            </div>

            {emotions && (
              <div className="border border-black/10 bg-white p-4" data-testid="mi-emotions">
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-3">
                  Live emotion read
                </div>
                <div className="space-y-2">
                  {Object.entries(emotions)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 5)
                    .map(([k, v]) => (
                      <div key={k} className="flex items-center gap-3 text-xs">
                        <div className="w-20 capitalize font-mono">{k}</div>
                        <div className="flex-1 h-2 bg-gray-100 relative">
                          <div
                            className="h-full transition-all"
                            style={{
                              width: `${Math.round(v * 100)}%`,
                              background: EMOTION_COLORS[k] || "#000",
                            }}
                          />
                        </div>
                        <div className="w-10 text-right font-mono">
                          {Math.round(v * 100)}%
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            <div className="border border-black/10 bg-[#F8F9FA] p-4 text-xs text-gray-600">
              <div className="flex items-start gap-2">
                <Sparkles className="w-3.5 h-3.5 text-[#0033FF] mt-0.5 flex-shrink-0" />
                <span>
                  <strong className="text-black">Privacy:</strong> your webcam video never leaves
                  your browser. Only the aggregated emotion scores are sent with each answer.
                </span>
              </div>
            </div>
          </div>
        </div>
        </>
      ) : result ? (
        // Result screen
        <div className="space-y-6" data-testid="mi-result">
          <div className="border border-black/10 bg-white p-8 flex flex-wrap items-center gap-6">
            <div className="relative">
              <svg width="140" height="140" viewBox="0 0 140 140">
                <circle cx="70" cy="70" r="62" stroke="#eee" strokeWidth="10" fill="none" />
                <circle
                  cx="70"
                  cy="70"
                  r="62"
                  stroke={
                    result.overall_score >= 80
                      ? "#34C759"
                      : result.overall_score >= 60
                      ? "#0033FF"
                      : "#FF3B30"
                  }
                  strokeWidth="10"
                  fill="none"
                  strokeDasharray={`${(result.overall_score / 100) * 389.5} 389.5`}
                  strokeDashoffset="0"
                  transform="rotate(-90 70 70)"
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="font-display font-black text-4xl tracking-tighter">
                  {result.overall_score}
                </div>
                <div className="font-mono text-[9px] uppercase tracking-widest text-gray-500">
                  /100
                </div>
              </div>
            </div>
            <div className="flex-1 min-w-[240px]">
              <Trophy className="w-5 h-5 text-[#FFD60A] mb-2" />
              <div className="font-display font-black text-3xl tracking-tighter">
                {result.overall_score >= 80
                  ? "Strong performance"
                  : result.overall_score >= 60
                  ? "Solid foundation"
                  : "Room to grow"}
              </div>
              <div className="text-sm text-gray-600 mt-2">{result.facial_summary}</div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={reset}
                className="bg-black text-white hover:bg-[#0033FF] font-bold px-5 py-3 flex items-center gap-2 transition-colors"
                data-testid="mi-restart"
              >
                <RotateCcw className="w-4 h-4" /> New interview
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="border border-[#34C759]/30 bg-[#34C759]/5 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Smile className="w-4 h-4 text-[#34C759]" />
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#34C759]">
                  Strengths
                </div>
              </div>
              <ul className="space-y-2 text-sm">
                {(result.strengths || []).map((s) => (
                  <li key={s} className="flex gap-2">
                    <span className="w-1 h-1 mt-2 bg-[#34C759] flex-shrink-0" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="border border-[#FF3B30]/30 bg-[#FF3B30]/5 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Frown className="w-4 h-4 text-[#FF3B30]" />
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF3B30]">
                  Weaknesses
                </div>
              </div>
              <ul className="space-y-2 text-sm">
                {(result.weaknesses || []).map((s) => (
                  <li key={s} className="flex gap-2">
                    <span className="w-1 h-1 mt-2 bg-[#FF3B30] flex-shrink-0" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="border border-[#0033FF]/30 bg-[#0033FF]/5 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-4 h-4 text-[#0033FF]" />
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#0033FF]">
                  Marcus's coaching
                </div>
              </div>
              <ul className="space-y-2 text-sm">
                {(result.advice || []).map((s) => (
                  <li key={s} className="flex gap-2">
                    <span className="w-1 h-1 mt-2 bg-[#0033FF] flex-shrink-0" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Per-answer reformulations — gold for B2-German learners */}
          {Array.isArray(result.reformulations) && result.reformulations.length > 0 && (
            <div className="mt-8" data-testid="mi-reformulations">
              <div className="flex items-center gap-2 mb-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/60">
                  How you could have answered better
                </div>
                {result.language === "de" && (
                  <span className="font-mono text-[10px] uppercase tracking-widest bg-white/10 px-2 py-0.5 rounded">
                    🇩🇪 B2-Niveau
                  </span>
                )}
              </div>
              <div className="space-y-4">
                {result.reformulations.map((r, i) => (
                  <div key={i} className="border border-white/10 bg-white/[0.03] p-5 rounded-xl">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-white/45 mb-1">
                      Question {i + 1}
                    </div>
                    <div className="font-bold text-sm leading-snug mb-4">{r.question}</div>

                    <div className="grid md:grid-cols-2 gap-3">
                      <div className="border-s-2 border-white/20 ps-3">
                        <div className="font-mono text-[9px] uppercase tracking-widest text-white/40 mb-1">
                          What you said
                        </div>
                        <div className="text-sm text-white/65 leading-relaxed italic">
                          "{r.candidate_said}"
                        </div>
                      </div>
                      <div className="border-s-2 border-white ps-3">
                        <div className="font-mono text-[9px] uppercase tracking-widest text-white mb-1">
                          A stronger version
                        </div>
                        <div className="text-sm text-white leading-relaxed">
                          {r.better_answer}
                        </div>
                      </div>
                    </div>

                    {r.why_better && (
                      <div className="mt-3 pt-3 border-t border-white/10 text-xs text-white/55 leading-relaxed">
                        <span className="font-mono uppercase tracking-widest text-white/45">Why →</span>{" "}
                        {r.why_better}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
